import { useCallback, useEffect, useMemo, useRef } from "react";

export interface UseDebounceControls<Args extends unknown[]> {
  /** The debounced function. Each call resets the timer; the callback fires once calls settle. */
  debounced: (...args: Args) => void;
  /** Cancel any pending invocation without firing it. */
  cancel: () => void;
  /** Immediately invoke the pending call (if any) with its latest args, clearing the timer. */
  flush: () => void;
}

/**
 * Debounces a callback: returns a stable `debounced` function that delays invoking `callback` until
 * `ms` milliseconds have passed without another call. Unlike {@link useDebouncedValue} (which
 * debounces a value), this debounces the *action*, so it fits event handlers that should run a
 * side effect (save, fetch, log) only after the user settles.
 *
 * The latest `callback` is always used when the timer fires (no stale closure), and the returned
 * `debounced`/`cancel`/`flush` references are stable across renders. Any pending call is cancelled
 * on unmount.
 *
 * @template Args - The argument tuple of the debounced callback
 * @param callback - The function to invoke after the debounce settles
 * @param [ms=300] - The debounce delay in milliseconds
 * @returns `{ debounced, cancel, flush }`
 *
 * @example
 * ```typescript
 * const { debounced: saveDraft, flush: saveNow } = useDebounce(
 *   (value: string) => persist(value),
 *   500
 * );
 *
 * <textarea
 *   onChange={(e) => saveDraft(e.target.value)}  // debounced while typing
 *   onBlur={saveNow}                              // persist immediately on blur
 * />
 * ```
 */
export const useDebounce = <Args extends unknown[]>(
  callback: (...args: Args) => void,
  ms: number = 300
): UseDebounceControls<Args> => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const pendingArgsRef = useRef<Args>(undefined);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    pendingArgsRef.current = undefined;
  }, []);

  const flush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    if (pendingArgsRef.current) {
      const args = pendingArgsRef.current;
      pendingArgsRef.current = undefined;
      callbackRef.current(...args);
    }
  }, []);

  const debounced = useCallback(
    (...args: Args) => {
      pendingArgsRef.current = args;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = undefined;
        pendingArgsRef.current = undefined;
        callbackRef.current(...args);
      }, ms);
    },
    [ms]
  );

  useEffect(() => cancel, [cancel]);

  return useMemo(() => ({ debounced, cancel, flush }), [debounced, cancel, flush]);
};
